import { datadogLogs } from '@datadog/browser-logs';
import type { Folder } from '@prisma/client';
import { DocumentType, FileType, Role } from '@prisma/client';
import type { UseInfiniteQueryResult, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { getFileIcon } from '@utils/browserFileHandling';
import { convertToSize } from '@utils/stringHelpers';
import type { ContextUseState } from '@utils/types';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import path from 'path';
import type { ParsedUrlQueryInput } from 'querystring';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import type { FormattedAttachment } from '~/components/pages/jobs/attachments';
import { getNextPageParam, useInfinite } from '~/hooks/useInfinite';
import type { DocumentRouterInput, DocumentRouterOutput, FolderRouterInput, FolderRouterOutput } from '~/server/routers/types';
import { toUploadableImageFile } from '~/utils/heicConversion';
import { trpc } from '~/utils/trpc';

export function getDocTypeColor(type: DocumentType) {
	switch (type) {
		case DocumentType.QUOTE:
			return 'bg-green-300 text-green-800';
		case DocumentType.SIGNED_QUOTE:
			return 'bg-green-300 text-green-800';
		case DocumentType.MEASUREMENT_PHOTO:
			return 'bg-orange-200 text-orange-800';
		case DocumentType.MEASUREMENT_MARKUP:
			return 'bg-orange-200 text-orange-800';
		case DocumentType.INSTALL_IMAGES:
			return 'bg-blue-300 text-blue-800';
		case DocumentType.INSTALL_DRAWING:
			return 'bg-blue-300 text-blue-800';
		case DocumentType.PURCHASE_ORDER:
			return 'bg-green-300 text-green-800';
		case DocumentType.INVOICE:
			return 'bg-green-300 text-green-800';
		case DocumentType.WORK_ORDER:
			return 'bg-pink-800 text-white';
		case DocumentType.OTHER:
			return 'bg-gray-300 text-gray-800';
		case DocumentType.USER_UPLOAD:
			return 'bg-primary text-white';
		default:
			return 'bg-gray-300 text-gray-800';
	}
}

export type DocumentsView = 'Grid' | 'List';

export interface IDocumentsState {
	activeDocument: [FormattedAttachment | undefined, Dispatch<SetStateAction<FormattedAttachment | undefined>>];
	activeDocumentIndex: number;
	addDocumentToFolderMutation: UseMutationResult<DocumentRouterOutput['addToFolder'], unknown, DocumentRouterInput['addToFolder']>;
	addDocumentsToFolderMutation: UseMutationResult<
		DocumentRouterOutput['addDocumentsToFolder'],
		unknown,
		DocumentRouterInput['addDocumentsToFolder']
	>;
	allFolders: (Folder & { isIndirectlySharedWithCustomer: boolean })[];
	checkedDocuments: [number[], Dispatch<SetStateAction<number[]>>];
	clearFilters: () => void;
	deleteCheckedDocuments: () => void;
	deleteDocument: UseMutationResult<DocumentRouterOutput['delete'], unknown, DocumentRouterInput['delete']>;
	documentData: FormattedAttachment[];
	attachmentsFormatter: (doc: DocumentRouterOutput['allPaged']['items'][number]) => FormattedAttachment;
	documentsQuery: UseInfiniteQueryResult<any>;
	downloadCheckedDocuments: () => void;
	drawerOpen: [boolean, Dispatch<SetStateAction<boolean>>];
	fetchNextPage: () => void;
	fileDropCallback: (file: File) => void;
	filters: {
		active: boolean;
		documentTypeFilter: [string[], Dispatch<SetStateAction<string[]>>];
		fileTypeFilter: [string[], Dispatch<SetStateAction<string[]>>];
	};
	folderId: number | null;
	/** Drops the attachment drawer params so the drawer closes instead of following you into another folder. */
	folderRoute: (folderId: number | null) => { pathname: string; query: ParsedUrlQueryInput };
	foldersQuery: UseQueryResult<FolderRouterOutput['allFolders']>;
	isDownloadingFiles: ContextUseState<boolean>;
	isLoading: [boolean, Dispatch<SetStateAction<boolean>>];
	navigateNext: () => void;
	navigatePrev: () => void;
	parentFolder?: (Folder & { isIndirectlySharedWithCustomer: boolean }) | null;
	previewOpen: [boolean, Dispatch<SetStateAction<boolean>>];
	isFillablePDFOpen: [boolean, Dispatch<SetStateAction<boolean>>];
	moveFolderMutation: UseMutationResult<FolderRouterOutput['moveFolder'], unknown, FolderRouterInput['moveFolder']>;
	router: NextRouter;
	searchHelpers: { changeView: (view: DocumentsView) => void; searchChange: (event: React.ChangeEvent<HTMLInputElement>) => void };
	searchQuery: [string, Dispatch<SetStateAction<string>>];
	setIsLoading: (isLoading: boolean) => void;
	setPreviewDisplay: (attachment: FormattedAttachment) => void;
	setPreviewDrawer: (attachment: FormattedAttachment) => void;
	toggleTechnicianPermissions: (adminOnly: boolean) => void;
	view: DocumentsView;
}

export const DocumentsContext = createContext<IDocumentsState | null>(null);

type Props = {
	children: ReactNode;
	jobId?: number;
};

export const useDocumentsContext = () => {
	const context = useContext(DocumentsContext);
	if (context == null) throw new Error('Documents Context not ready');
	return context;
};

export const DocumentsProvider = ({ children, jobId }: Props) => {
	const router = useRouter();
	const { view, folder } = router.query;
	const folderId = folder ? parseInt(folder as string) : null;

	const [activeDocument, setActiveDocument] = useState<FormattedAttachment>();
	const [checkedDocuments, setCheckedDocuments] = useState<number[]>([]);
	const [documentTypeFilter, setDocumentTypeFilter] = useState<string[]>([]);
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const [fileTypeFilter, setFileTypeFilter] = useState<string[]>([]);
	const [filtersActive, setFiltersActive] = useState<boolean>(false);
	const [isDownloadingFiles, setIsDownloadingFiles] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [previewOpen, setPreviewOpen] = useState<boolean>(false);
	const [isFillablePDFOpen, setIsFillablePDFOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState<string>('');

	const { client } = trpc.useUtils();

	const addDocumentToFolderMutation = trpc.documents.addToFolder.useMutation({
		async onSuccess(response, { folderId: targetFolderId, id }) {
			closeDrawerIfMovedOut({ documentIds: [id], targetFolderId });

			if (response.emailedContacts) {
				toast.success(
					`Document shared with customer and link sent to: ${response.emailedContacts.map((contact) => contact.email).join(', ')}`,
					{
						position: 'top-right',
						autoClose: false,
						closeOnClick: false,
						draggable: false,
						hideProgressBar: true,
						theme: 'light',
					}
				);
			} else if (response.error) {
				toast.warn(
					`Document shared with customer, but no email was sent. ${response.error} Please copy the shareable link to the clipboard and send it to your customer.`,
					{
						position: 'top-right',
						autoClose: false,
						closeOnClick: false,
						draggable: false,
						hideProgressBar: true,
						theme: 'light',
					}
				);
			}

			addDocumentToFolderMutation.reset();
		},
	});

	const addDocumentsToFolderMutation = trpc.documents.addDocumentsToFolder.useMutation({
		async onSuccess(response, { documentIds, folderId: targetFolderId }) {
			closeDrawerIfMovedOut({ documentIds, targetFolderId });

			if (response.emailedContacts) {
				toast.success(
					`Documents shared with customer and link sent to: ${response.emailedContacts.map((contact) => contact.email).join(', ')}`,
					{
						position: 'top-right',
						autoClose: false,
						closeOnClick: false,
						draggable: false,
						hideProgressBar: true,
						theme: 'light',
					}
				);
			} else if (response.error) {
				toast.warn(
					`Documents shared with customer, but no email was sent. ${response.error} Please copy the shareable link to the clipboard and send it to your customer.`,
					{
						position: 'top-right',
						autoClose: false,
						closeOnClick: false,
						draggable: false,
						hideProgressBar: true,
						theme: 'light',
					}
				);
			}

			setCheckedDocuments([]);
			addDocumentsToFolderMutation.reset();
		},
	});

	const moveFolderMutation = trpc.folders.moveFolder.useMutation({
		onSuccess({ isShared, isSharedChanged }) {
			if (isSharedChanged) {
				toast.info(
					isShared
						? 'Folder is now shared with the customer through its new parent folder.'
						: 'Folder is no longer shared with the customer.'
				);
			}
		},
	});

	useEffect(() => {
		if (fileTypeFilter.length > 0 || documentTypeFilter.length > 0) {
			setFiltersActive(true);
		} else {
			setFiltersActive(false);
		}
	}, [fileTypeFilter, documentTypeFilter]);

	const clearFilters = () => {
		setFileTypeFilter([]);
		setDocumentTypeFilter([]);
		setFiltersActive(false);
	};

	const setPreviewDrawer = (attachment: FormattedAttachment) => {
		setActiveDocument(attachment);
		void router.push({ pathname: router.pathname, query: { ...router.query, drawerOpen: true, attachmentId: attachment.id } });
	};

	const setPreviewDisplay = (attachment: FormattedAttachment) => {
		setActiveDocument(attachment);
		setPreviewOpen(true);
	};

	const allFoldersQuery = trpc.folders.allFolders.useQuery({ jobId: jobId ?? null });
	const foldersQuery = trpc.folders.allFolders.useQuery({ jobId: jobId ?? null, folderId });
	const {
		query: documentsQuery,
		data: documents,
		fetchNextPage,
	} = useInfinite(
		trpc.documents.allPaged.useInfiniteQuery(
			{
				documentTypeFilter,
				fileTypeFilter,
				folderId,
				includePreviewLinks: true,
				jobId: jobId ?? null,
				limit: 20,
				searchQuery,
			},
			{ getNextPageParam }
		)
	);
	const deleteDocument = trpc.documents.delete.useMutation({
		onSuccess() {
			setDrawerOpen(false);
			setActiveDocument(undefined);
			setPreviewOpen(false);
			setIsFillablePDFOpen(false);
			changeView('Grid');
		},
	});
	const deleteDocuments = trpc.documents.deleteMany.useMutation({
		onSuccess() {
			setDrawerOpen(false);
			setActiveDocument(undefined);
			setPreviewOpen(false);
			setIsFillablePDFOpen(false);
			setCheckedDocuments([]);
			changeView('List');
		},
	});
	const downloadDocuments = trpc.documents.generateMultiFileDownloadData.useMutation({
		onSuccess(data) {
			const zip = new JSZip();
			void Promise.all(
				data.map(async (fileDetails) => ({
					...fileDetails,
					content: await (await fetch(fileDetails.downloadUrl)).blob(),
				}))
			).then((fileDetailsWithContent) => {
				fileDetailsWithContent.forEach((fileDetails, index) => {
					zip.file(fileDetails.fileName ?? `Unknown File ${index}`, fileDetails.content);
				});
				void zip.generateAsync({ type: 'blob' }).then((content) => {
					saveAs(content, jobId !== null ? `J${jobId}_files.zip` : 'document_hub_files.zip');
					setIsDownloadingFiles(false);
					setCheckedDocuments([]);
				});
			});
		},
	});
	const downloadCheckedDocuments = () => {
		setIsDownloadingFiles(true);
		downloadDocuments.mutate({ ids: checkedDocuments });
	};

	const updateDocumentPermissions = trpc.documents.updateRoles.useMutation();

	const toggleTechnicianPermissions = (adminOnly: boolean) => {
		// This function should work as it isn't callable without the drawer being open which sets an active attachment
		// Additionally the else if prevents technicians accidentally being set to full access
		if (activeDocument) {
			if (!adminOnly) {
				updateDocumentPermissions.mutate({ id: activeDocument.id as number, roles: [Role.FULL, Role.TECHNICIAN] });
			} else {
				updateDocumentPermissions.mutate({ id: activeDocument.id as number, roles: [Role.FULL] });
			}
		}
	};

	const searchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(event.target.value);
	};

	const folderRoute: IDocumentsState['folderRoute'] = (folderId) => {
		const { attachmentId: _attachmentId, drawerOpen: _drawerOpen, folder: _folder, ...rest } = router.query;
		return { pathname: router.pathname, query: folderId === null ? rest : { ...rest, folder: folderId } };
	};

	const closeDrawerIfMovedOut = ({ documentIds, targetFolderId }: { documentIds: number[]; targetFolderId: number | null }) => {
		if (!drawerOpen || !activeDocument || !documentIds.includes(activeDocument.id) || targetFolderId === folderId) return;
		void router.push(folderRoute(folderId)).then(() => setActiveDocument(undefined));
	};

	const changeView = (view: DocumentsView) => {
		void router.push({
			pathname: router.pathname,
			query: { ...router.query, view },
		});
	};

	const deleteCheckedAttachments = () => {
		deleteDocuments.mutate({ ids: checkedDocuments });
	};

	const typeFilters =
		fileTypeFilter.length > 0 && documentTypeFilter.length > 0
			? documents.filter((document) => {
					if (fileTypeFilter.includes(document.fileType)) {
						return document;
					}
					if (documentTypeFilter.includes(document.documentType)) {
						return document;
					}
					if (fileTypeFilter.includes(document.fileType) && documentTypeFilter.includes(document.documentType)) {
						return document;
					}
					return false;
				})
			: documents;

	const attachmentsFormatter = (doc: DocumentRouterOutput['allPaged']['items'][number]) => {
		const { fileType } = doc;
		const fileTypeInfo = getFileIcon(fileType);
		const icon = fileTypeInfo.icon;
		const type = fileTypeInfo.type;

		return {
			id: doc.id,
			title: doc.name,
			source: doc.preSignedPreviewUrl,
			rawSource: doc.location,
			date: doc.createdAt,
			size: convertToSize(doc.size),
			uploadedBy: doc.uploadedByUser,
			allowedRoles: doc.allowedRoles,
			type: doc.documentType,
			fileType: fileType,
			prettyType: type,
			fileIcon: icon,
			rawData: doc,
			folderId: doc.folderId,
			viewedByCustomerAt: doc.viewedByCustomerAt,
			isCustomerSignatureRequired: doc.isCustomerSignatureRequired,
			signedByCustomerAt: doc.signedByCustomerAt,
		} as FormattedAttachment;
	};

	const formattedAttachments = typeFilters.map((imageData) => attachmentsFormatter(imageData));

	const activeDocumentIndex = formattedAttachments.findIndex((d) => d.id === activeDocument?.id);

	const navigatePrev = () => {
		if (activeDocumentIndex < 0 || formattedAttachments.length === 0) return;
		const target = formattedAttachments[(activeDocumentIndex - 1 + formattedAttachments.length) % formattedAttachments.length]!;
		drawerOpen ? setPreviewDrawer(target) : setPreviewDisplay(target);
	};

	const navigateNext = () => {
		if (activeDocumentIndex < 0 || formattedAttachments.length === 0) return;
		const target = formattedAttachments[(activeDocumentIndex + 1) % formattedAttachments.length]!;
		drawerOpen ? setPreviewDrawer(target) : setPreviewDisplay(target);
	};

	const uploadFileMutation = trpc.documents.create.useMutation({
		async onSuccess() {
			setIsLoading(false);
		},
	});

	const fileDropCallback = async (droppedFile: File) => {
		setIsLoading(true);

		let file: File;
		try {
			file = await toUploadableImageFile(droppedFile);
		} catch (error) {
			datadogLogs.logger.error(`Could not convert ${droppedFile.name} to JPEG: ${error}`);

			toast.error(`${droppedFile.name} could not be converted to JPEG.`, { autoClose: false });

			setIsLoading(false);

			throw error;
		}
		const filename = file.name;
		const fileName = path.parse(filename).name;
		const extension = path.parse(filename).ext.substring(1);
		const matchFileType: string = extension.toUpperCase();

		if (!Object.values(FileType).includes(matchFileType as FileType)) {
			datadogLogs.logger.error(`${matchFileType} File Type not supported.`);

			toast.error(`${matchFileType} is not a supported upload type.`, { autoClose: false });

			setIsLoading(false);

			throw new Error(`File Type ${matchFileType} not supported.`);
		}
		const s3Destination = await client.documents.generatePreSignedUrl.mutate({
			fileName,
			jobId: jobId ?? null,
		});
		const uploadResponse = await fetch(s3Destination.preSignedUploadUrl, {
			method: 'PUT',
			body: file,
		});

		if (uploadResponse.status != 200) {
			datadogLogs.logger.error(`Error uploading file to S3: ${uploadResponse.status} - ${uploadResponse.statusText}`);

			toast.error(`Error uploading file to server.`, { autoClose: false });

			setIsLoading(false);

			throw new Error('There was a problem uploading the file to S3');
		}
		uploadFileMutation.mutate({
			bucket: s3Destination.s3Bucket,
			documentType: DocumentType.USER_UPLOAD,
			fileType: matchFileType as FileType,
			folderId: folderId ?? undefined,
			jobId: jobId ?? null,
			key: s3Destination.s3Key,
			location: s3Destination.s3Location,
			name: fileName,
			size: file.size,
		});
	};
	const parentFolderId = allFoldersQuery.data?.find((folder) => folder.id == folderId)?.parentFolderId;
	const documentContextValues: IDocumentsState = {
		activeDocumentIndex,
		router,
		view: view as DocumentsView,
		searchQuery: [searchQuery, setSearchQuery],
		searchHelpers: {
			searchChange,
			changeView,
		},
		filters: {
			active: filtersActive,
			fileTypeFilter: [fileTypeFilter, setFileTypeFilter],
			documentTypeFilter: [documentTypeFilter, setDocumentTypeFilter],
		},
		clearFilters,
		checkedDocuments: [checkedDocuments, setCheckedDocuments],
		activeDocument: [activeDocument, setActiveDocument],
		drawerOpen: [drawerOpen, setDrawerOpen],
		previewOpen: [previewOpen, setPreviewOpen],
		isFillablePDFOpen: [isFillablePDFOpen, setIsFillablePDFOpen],
		setPreviewDisplay,
		setPreviewDrawer,
		toggleTechnicianPermissions,
		documentsQuery,
		foldersQuery,
		documentData: formattedAttachments,
		attachmentsFormatter,
		fetchNextPage,
		deleteDocument: deleteDocument,
		isLoading: [isLoading, setIsLoading],
		navigateNext,
		navigatePrev,
		setIsLoading,
		fileDropCallback,
		folderId,
		folderRoute,
		allFolders: allFoldersQuery.data ?? [],
		parentFolder: allFoldersQuery.data?.find((folder) => folder.id == parentFolderId),
		addDocumentToFolderMutation,
		addDocumentsToFolderMutation,
		moveFolderMutation,
		deleteCheckedDocuments: deleteCheckedAttachments,
		downloadCheckedDocuments,
		isDownloadingFiles: [isDownloadingFiles, setIsDownloadingFiles],
	};

	return <DocumentsContext.Provider value={documentContextValues}>{children}</DocumentsContext.Provider>;
};
